import { useMemo, useState } from "react";
import {
  CardGiftcard,
  EmojiEvents,
  AttachMoney,
  AutoAwesome,
  AccessTime,
} from "@mui/icons-material";
import { Wheel } from "react-custom-roulette";
import SpinWheelSuccess from "../../components/dashboard/spinwheelsuccess";
import { wheelPrizes } from "../../lib/consts";
import { useDispatch, useSelector } from "react-redux";
import { ClaimSpinReward } from "../../redux/slices/gift";
import useWheelTimer from "../../hooks/useWheelTimer";

const SpinWheelPage = () => {
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const targetPrizes = [wheelPrizes[0], wheelPrizes[1]];
  const availableRewards = useSelector((state) => state.gift.available);

  const spinWheel = () => {
    if (mustSpin) return;
    setShowResult(false);
    setSelectedPrize(null);

    // Determine which prize to land on
    let selectedIndex;
    if (targetPrizes.length >= 2) {
      // Select randomly from target prizes
      const availableIndices = targetPrizes.map((p, idx) => ({
        prize: p,
        index: idx,
      }));
      const randomPick =
        availableIndices[Math.floor(Math.random() * availableIndices.length)];
      selectedIndex = randomPick.index;
    } else {
      // Random selection from all prizes
      selectedIndex = Math.floor(Math.random() * wheelPrizes.length);
    }

    setPrizeNumber(selectedIndex);
    setMustSpin(true);
  };

  const handleStopSpinning = () => {
    setMustSpin(false);
    setSelectedPrize(wheelPrizes[prizeNumber]);
    setShowResult(true);
  };

  const resetWheel = () => {
    setShowResult(false);
    setSelectedPrize(null);
  };

  const [wheelReward, remainingTime, expired] = useWheelTimer(availableRewards);

  const renderedButtonContent = useMemo(() => {
    if (mustSpin) {
      <span className="flex items-center gap-3">
        <AutoAwesome className="animate-spin" size={24} />
        SPINNING...
      </span>;
    } else if (expired || !wheelReward) {
      return <span className="">Renewal in progress</span>;
    } else if (remainingTime) {
      return (
        <span className="flex items-center gap-3">
          <AccessTime size={24} />
          {remainingTime}
        </span>
      );
    }

    return (
      <span className="flex items-center gap-3">
        <AutoAwesome size={24} />
        SPIN
        <AutoAwesome size={24} />
      </span>
    );
  }, [expired, mustSpin, remainingTime, wheelReward]);

  const disabled = mustSpin || remainingTime || expired | !wheelReward;

  return (
    <div className="min-h-screen p-1 xs:p-2 sm:p-4 w-full">
      {/* Header with glow effect */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <EmojiEvents
            className="text-[#ff9c19] animate-pulse"
            size={36}
            style={{ filter: "drop-shadow(0 0 10px rgba(255,156,25,0.8))" }}
          />
          <h1
            className="text-3xl sm:text-5xl font-bold text-white"
            style={{
              textShadow:
                "0 0 20px rgba(255,156,25,0.5), 0 4px 8px rgba(0,0,0,0.8)",
            }}
          >
            Spin & Win!
          </h1>
          <CardGiftcard
            className="text-[#e2008a] animate-pulse"
            size={36}
            style={{ filter: "drop-shadow(0 0 10px rgba(226,0,138,0.8))" }}
          />
        </div>
        <p className="text-white/80 text-md sm:text-lg font-medium">
          Try your luck and win amazing cash rewards!
        </p>
      </div>
      <div className="flex flex-col spinwheel:flex-row items-center spinwheel:items-start justify-around w-full">
        {/* Premium Wheel Container */}
        <div
          className="rounded-3xl p-3 sm:!p-8 mb-6 relative overflow-hidden max-w-[511px]"
          style={{
            backgroundColor: "#3b0709",
            boxShadow:
              "0 20px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,156,25,0.2)",
          }}
        >
          {/* Decorative corners */}
          <div
            className="absolute top-0 left-0 w-20 h-20"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,156,25,0.1) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute top-0 right-0 w-20 h-20"
            style={{
              background:
                "linear-gradient(225deg, rgba(255,156,25,0.1) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute bottom-0 left-0 w-20 h-20"
            style={{
              background:
                "linear-gradient(45deg, rgba(226,0,138,0.1) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute bottom-0 right-0 w-20 h-20"
            style={{
              background:
                "linear-gradient(315deg, rgba(226,0,138,0.1) 0%, transparent 70%)",
            }}
          />

          <div className="relative flex justify-center items-center">
            {/* Wheel with premium styling */}
            <div
              className="relative"
              style={{
                filter: "drop-shadow(0 15px 35px rgba(0,0,0,0.9))",
                transform: "rotate(-45deg)",
              }}
            >
              <div
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,156,25,0.3) 0%, rgba(255,156,25,0) 70%)",
                  position: "absolute",
                  inset: "-20px",
                  borderRadius: "50%",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />
              <Wheel
                mustStartSpinning={mustSpin}
                prizeNumber={prizeNumber}
                data={wheelPrizes}
                onStopSpinning={handleStopSpinning}
                backgroundColors={[
                  "#3f297e",
                  "#175fa9",
                  "#169ed8",
                  "#239b63",
                  "#64b031",
                  "#efe61f",
                  "#f7a416",
                  "#e6471d",
                  "#dc0936",
                ]}
                textColors={["#ffffff"]}
                outerBorderColor="#1a1a1a"
                outerBorderWidth={3}
                innerBorderColor="#1a1a1a"
                innerBorderWidth={2}
                innerRadius={20}
                radiusLineColor="#1a1a1a"
                radiusLineWidth={2}
                fontSize={20}
                fontWeight="bold"
                fontFamily="Poppins"
                textDistance={65}
                spinDuration={0.3}
                // pointerProps={{
                //   style: {
                //     color: "#ffdd2d",
                //     borderTopColor: "#ff9c19",
                //     filter:
                //       "drop-shadow(0 0 15px rgba(255,156,25,0.9)) drop-shadow(0 6px 12px rgba(0,0,0,0.8))",
                //   },
                // }}
              />
            </div>
          </div>

          {/* Premium Spin Button */}
          <div className="text-center mt-8">
            <button
              onClick={spinWheel}
              disabled={disabled}
              className="px-7 py-3 rounded-2xl text-xl font-bold transition-all duration-200 relative group"
              style={{
                color: disabled ? "rgba(26,26,26,0.6)" : "#1a1a1a",
                backgroundImage: disabled
                  ? "linear-gradient(0deg, rgba(255,156,25,0.3) 40%, rgba(255,221,45,0.3) 110%)"
                  : "linear-gradient(0deg, rgb(255,156,25) 40%, rgb(255,221,45) 110%)",
                boxShadow: disabled
                  ? "0 4px 15px rgba(255,156,25,0.2)"
                  : "0 8px 30px rgba(255,156,25,0.6), 0 2px 8px rgba(255,221,45,0.4)",
                border: disabled
                  ? "2px solid rgba(255,221,45,0.3)"
                  : "2px solid rgba(255,221,45,0.5)",
                opacity: disabled ? 0.7 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!mustSpin && !remainingTime && !expired && wheelReward) {
                  e.target.style.backgroundImage =
                    "linear-gradient(0deg, rgb(255,176,55) 40%, rgb(255,231,75) 110%)";
                  e.target.style.transform = "translateY(-2px) scale(1.05)";
                  e.target.style.boxShadow =
                    "0 12px 40px rgba(255,156,25,0.8), 0 4px 12px rgba(255,221,45,0.6)";
                }
              }}
              onMouseLeave={(e) => {
                if (!mustSpin && !remainingTime && !expired & wheelReward) {
                  e.target.style.backgroundImage =
                    "linear-gradient(0deg, rgb(255,156,25) 40%, rgb(255,221,45) 110%)";
                  e.target.style.transform = "translateY(0) scale(1)";
                  e.target.style.boxShadow =
                    "0 8px 30px rgba(255,156,25,0.6), 0 2px 8px rgba(255,221,45,0.4)";
                }
              }}
            >
              {renderedButtonContent}
            </button>
          </div>
        </div>

        {/* Premium Prize List */}
        <div
          className="rounded-3xl mb-16 md:mb-6 p-8 max-w-[511px] w-full"
          style={{
            backgroundColor: "#3b0709",
            boxShadow:
              "0 20px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,156,25,0.2)",
          }}
        >
          <h3
            className="text-2xl font-bold text-white mb-6 text-center"
            style={{
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          >
            💰 Available Prizes 💰
          </h3>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
            {wheelPrizes.map((prize, index) => (
              <div
                key={index}
                className="rounded-xl p-4 text-center transition-all duration-200 hover:scale-110 cursor-pointer"
                style={{
                  backgroundColor: "#280203",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                  border: `2px solid ${prize.style.backgroundColor}40`,
                }}
              >
                <AttachMoney
                  className="mx-auto mb-2"
                  style={{
                    color: prize.style.backgroundColor,
                    filter: `drop-shadow(0 0 8px ${prize.style.backgroundColor}80)`,
                  }}
                  size={28}
                />
                <p className="text-white font-bold text-lg">{prize.option}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <SpinWheelSuccess
        showResult={showResult}
        selectedPrize={selectedPrize}
        resetWheel={resetWheel}
        onClose={() => setShowResult(false)}
        wheelReward={wheelReward}
      />

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px) scale(0.9); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </div>
  );
};

export default SpinWheelPage;
